import React, { useEffect, useState, useRef } from "react";
import { useStore } from "./store";
import api from "./api";
import { createChart, CandlestickSeries, HistogramSeries } from "lightweight-charts";
import {
  CreditCard, ShoppingBag, BarChart3, Bot, ArrowLeftRight, X,
  Copy, Check, Shield, Zap, Send, Sparkles, TrendingUp, TrendingDown,
  MessageCircle, Search, MapPin, Star, ArrowUpRight, ArrowDownRight
} from "lucide-react";

/* ═════════════════════════════════════════════════════════════════
   VIRTUAL VISA CARD SCREEN
═════════════════════════════════════════════════════════════════ */
export function CardScreen() {
  const { user, wallets, tgcPrice } = useStore();
  const [showDetails, setShowDetails] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const eur = ((wallets?.shoppingTgc || 0) * tgcPrice).toFixed(2);

  const cardData = {
    maskedPan: "4111 •••• •••• 1234",
    fullPan: "4111 2345 6789 1234",
    exp: "12/27",
    cvv: "742",
  };

  const copy = (txt, field) => {
    navigator.clipboard.writeText(txt);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">💳 Visa Card</h1>
        <p className="screen-subtitle">DEMO · Virtual Turbo Visa</p>
      </div>
      <div style={{ padding: 20 }}>
        {/* Card visual */}
        <div style={{ perspective: 1000, marginBottom: 20 }}>
          <div
            onClick={() => setFlipped(!flipped)}
            style={{
              position: "relative", width: "100%", aspectRatio: "1.6 / 1",
              cursor: "pointer", transition: "transform 0.8s", transformStyle: "preserve-3d",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0)",
            }}>
            {/* FRONT */}
            <div style={{
              position: "absolute", inset: 0, backfaceVisibility: "hidden", borderRadius: 20,
              background: "linear-gradient(135deg, #0D1B4B 0%, #1A3A8F 50%, #0E6B5E 100%)",
              padding: 22, color: "white", boxShadow: "0 10px 30px rgba(13,27,75,0.4)",
            }}>
              <div className="row between" style={{ marginBottom: 30 }}>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 900 }}>T<span style={{ fontSize: 14, letterSpacing: 2, marginLeft: 4 }}>TURBO</span></div>
                </div>
                <div style={{ fontSize: 10, opacity: 0.6, letterSpacing: 2 }}>VIRTUAL</div>
              </div>
              <div style={{ width: 44, height: 32, borderRadius: 6, background: "rgba(232,160,32,0.85)", marginBottom: 14 }} />
              <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: 3, fontFamily: "monospace" }}>
                {showDetails ? cardData.fullPan : cardData.maskedPan}
              </div>
              <div className="row between mt-md" style={{ alignItems: "flex-end" }}>
                <div>
                  <div style={{ fontSize: 9, opacity: 0.6, letterSpacing: 1 }}>ДЕРЖАТЕЛЬ</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{user?.firstName?.toUpperCase()} {user?.lastName?.toUpperCase()}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, opacity: 0.6, letterSpacing: 1 }}>СРОК</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{cardData.exp}</div>
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, fontStyle: "italic", letterSpacing: -1 }}>VISA</div>
              </div>
            </div>
            {/* BACK */}
            <div style={{
              position: "absolute", inset: 0, backfaceVisibility: "hidden",
              transform: "rotateY(180deg)", borderRadius: 20,
              background: "#1F2937", padding: 22, color: "white",
            }}>
              <div style={{ height: 40, background: "black", marginLeft: -22, marginRight: -22, marginTop: 14 }} />
              <div className="row gap-sm mt-md">
                <div style={{ flex: 1, height: 32, background: "rgba(255,255,255,0.85)", borderRadius: 4 }} />
                <div style={{ background: "white", padding: 6, borderRadius: 4, minWidth: 60, textAlign: "center" }}>
                  <div style={{ fontSize: 8, color: "#666" }}>CVV</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1A1A2E", letterSpacing: 3 }}>
                    {showDetails ? cardData.cvv : "•••"}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 9, opacity: 0.4, textAlign: "center", marginTop: 20 }}>
                Нажми чтобы перевернуть
              </div>
            </div>
          </div>
        </div>

        {/* Balance */}
        <div className="card mb-md">
          <div className="row between">
            <div>
              <div className="text-hint text-sm">Доступно к оплате</div>
              <div className="text-2xl text-bold text-navy">€{eur}</div>
              <div className="text-sm text-muted">{(wallets?.shoppingTgc || 0).toFixed(2)} TGC из Shopping</div>
            </div>
            <button className="btn btn-teal" onClick={() => useStore.getState().setToast("info", "Пополнение из Shopping Wallet (см. вкладку Кошелёк)")}>
              <Plus size={16} /> Пополнить
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="grid-2 mb-md">
          <button className="btn btn-outline" onClick={() => setShowDetails(!showDetails)} data-testid="card-toggle-details">
            {showDetails ? "Скрыть" : "Реквизиты"}
          </button>
          <button className="btn btn-outline" onClick={() => useStore.getState().setToast("info", "Карта заблокирована (DEMO)")}>
            🔒 Заблокировать
          </button>
        </div>

        {showDetails && (
          <div className="card">
            <div className="text-bold mb-sm">Реквизиты карты</div>
            {[
              ["Номер", cardData.fullPan, "pan"],
              ["Срок", cardData.exp, "exp"],
              ["CVV", cardData.cvv, "cvv"],
            ].map(([label, val, key]) => (
              <div key={key} className="row between" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div>
                  <div className="text-hint text-sm">{label}</div>
                  <div className="text-bold" style={{ fontFamily: "monospace" }}>{val}</div>
                </div>
                <button onClick={() => copy(val, key)} style={{ background: "var(--bg)", border: "none", padding: 8, borderRadius: 8, cursor: "pointer" }}>
                  {copiedField === key ? <Check size={14} color="#10B981" /> : <Copy size={14} />}
                </button>
              </div>
            ))}
            <p className="text-hint text-sm mt-sm">🛡️ PCI DSS. Не передавай CVV третьим лицам.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   MARKETPLACE
═════════════════════════════════════════════════════════════════ */
export function MarketplaceScreen() {
  const { products, buyProduct, wallets } = useStore();
  const [cat, setCat] = useState("All");
  const cats = ["All", ...new Set(products.map((p) => p.category))];
  const filtered = cat === "All" ? products : products.filter((p) => p.category === cat);

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">🛒 Marketplace</h1>
        <p className="screen-subtitle">Phygital товары за TGC</p>
      </div>
      <div style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12 }}>
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)}
              className={`chip ${cat === c ? "gold" : ""}`}
              style={{ cursor: "pointer", padding: "8px 14px", border: "none", fontSize: 12 }}>
              {c}
            </button>
          ))}
        </div>
        <div className="grid-2">
          {filtered.map((p) => (
            <div key={p.productId} className="card" style={{ padding: 0, overflow: "hidden" }}>
              <img src={p.image} alt={p.name} style={{ width: "100%", height: 120, objectFit: "cover" }}
                onError={(e) => { e.target.style.display = "none"; }} />
              <div style={{ padding: 12 }}>
                <div className="text-bold text-sm" style={{ minHeight: 36 }}>{p.name}</div>
                <div className="text-hint text-sm">{p.category}</div>
                <div className="text-bold text-navy mt-sm">€{p.priceEur}</div>
                <div className="text-sm text-muted">{p.priceTGC.toFixed(0)} TGC</div>
                <button className="btn btn-teal" style={{ width: "100%", padding: 8, marginTop: 8, fontSize: 12 }}
                  disabled={wallets && wallets.shoppingTgc < p.priceTGC}
                  onClick={() => buyProduct(p.productId)} data-testid={`buy-${p.productId}`}>
                  {wallets && wallets.shoppingTgc < p.priceTGC ? "Мало TGC" : "Купить"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   EXCHANGE — Turbo Exchange with TradingView-style chart
═════════════════════════════════════════════════════════════════ */
export function ExchangeScreen() {
  const [pairs, setPairs] = useState([]);
  const [pair, setPair] = useState("TGC/USDT");
  const [interval_, setInterval] = useState("1h");
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("");
  const [price, setPrice] = useState("");
  const [orderType, setOrderType] = useState("market");
  const chartRef = useRef(null);
  const chartApi = useRef(null);

  const { tradeOrder } = useStore();

  useEffect(() => { api.get("/exchange/pairs").then((r) => setPairs(r.data.pairs)); }, []);

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = createChart(chartRef.current, {
      width: chartRef.current.clientWidth,
      height: 280,
      localization: { locale: "en-US", priceFormatter: (p) => `$${p.toFixed(4)}` },
      layout: { background: { color: "#0D1B4B" }, textColor: "#fff" },
      grid: { vertLines: { color: "rgba(255,255,255,0.05)" }, horzLines: { color: "rgba(255,255,255,0.05)" } },
      timeScale: { timeVisible: true, borderColor: "rgba(255,255,255,0.1)" },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.1)" },
    });
    chartApi.current = chart;
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#10B981", downColor: "#EF4444",
      borderUpColor: "#10B981", borderDownColor: "#EF4444",
      wickUpColor: "#10B981", wickDownColor: "#EF4444",
    });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      color: "rgba(232,160,32,0.5)",
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });

    api.get(`/exchange/candles?pair=${encodeURIComponent(pair)}&interval=${interval_}&limit=100`)
      .then((r) => {
        const candles = r.data.candles;
        candleSeries.setData(candles);
        volumeSeries.setData(candles.map((c) => ({
          time: c.time,
          value: c.volume,
          color: c.close > c.open ? "rgba(16,185,129,0.5)" : "rgba(239,68,68,0.5)"
        })));
        chart.timeScale().fitContent();
      });

    const resize = () => chart.resize(chartRef.current?.clientWidth || 350, 280);
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      try { chart.remove(); } catch {}
    };
  }, [pair, interval_]);

  const currentPair = pairs.find((p) => p.symbol === pair);
  return (
    <div className="screen">
      <div className="screen-header" style={{ padding: 12 }}>
        <div className="row between">
          <select value={pair} onChange={(e) => setPair(e.target.value)}
            style={{ background: "rgba(255,255,255,0.15)", color: "white", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 15, fontWeight: 700 }}
            data-testid="pair-selector">
            {pairs.map((p) => <option key={p.symbol} value={p.symbol} style={{ color: "black" }}>{p.symbol}</option>)}
          </select>
          {currentPair && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>${currentPair.price.toLocaleString()}</div>
              <div className={currentPair.change24h >= 0 ? "text-success" : "text-error"} style={{ fontSize: 12, fontWeight: 600 }}>
                {currentPair.change24h >= 0 ? "+" : ""}{currentPair.change24h}% 24h
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div ref={chartRef} style={{ width: "100%", height: 280, background: "#0D1B4B" }} />

      {/* Interval selector */}
      <div style={{ padding: "10px 16px", display: "flex", gap: 6, overflowX: "auto" }}>
        {["1m", "5m", "15m", "1h", "4h", "1d"].map((iv) => (
          <button key={iv} onClick={() => setInterval(iv)}
            className={`chip ${interval_ === iv ? "gold" : ""}`}
            style={{ cursor: "pointer", padding: "6px 12px", border: "none", fontSize: 11 }}>
            {iv}
          </button>
        ))}
      </div>

      {/* Trade panel */}
      <div style={{ padding: 16 }}>
        <div className="grid-2 mb-md">
          <button className={`btn ${side === "buy" ? "btn-teal" : "btn-outline"}`} onClick={() => setSide("buy")} data-testid="side-buy">
            <TrendingUp size={16} /> BUY
          </button>
          <button className={`btn ${side === "sell" ? "btn-teal" : "btn-outline"}`}
            style={side === "sell" ? { background: "linear-gradient(135deg,#EF4444,#F87171)" } : {}}
            onClick={() => setSide("sell")} data-testid="side-sell">
            <TrendingDown size={16} /> SELL
          </button>
        </div>
        <div className="grid-2 mb-md">
          <button className={`chip ${orderType === "market" ? "gold" : ""}`} onClick={() => setOrderType("market")} style={{ cursor: "pointer" }}>
            Market
          </button>
          <button className={`chip ${orderType === "limit" ? "gold" : ""}`} onClick={() => setOrderType("limit")} style={{ cursor: "pointer" }}>
            Limit
          </button>
        </div>
        {orderType === "limit" && (
          <div className="mb-md">
            <label className="input-label">Цена (USDT)</label>
            <input type="number" className="input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="order-price" />
          </div>
        )}
        <label className="input-label">Количество</label>
        <input type="number" className="input mb-md" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="order-amount" />
        <button className="btn btn-primary btn-full"
          style={{ background: side === "buy" ? "var(--success)" : "var(--error)" }}
          disabled={!amount}
          onClick={() => tradeOrder(pair, side, parseFloat(amount), orderType === "limit" ? parseFloat(price) : null)}
          data-testid="order-submit">
          {side.toUpperCase()} {amount || 0} {pair.split("/")[0]}
        </button>
        <p className="text-hint text-sm text-center mt-md">
          💡 DEMO режим — ордера исполняются мгновенно с демо-ликвидностью
        </p>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   P2P EXCHANGE
═════════════════════════════════════════════════════════════════ */
export function P2PScreen() {
  const [offers] = useState([
    { id: 1, user: "Иван К.", rating: 4.9, trades: 123, price: 0.102, amount: "100-5000", currency: "KZT", method: "Kaspi Gold" },
    { id: 2, user: "Aida M.",  rating: 5.0, trades: 45,  price: 0.098, amount: "500-10000", currency: "USD", method: "Wise" },
    { id: 3, user: "Marat Z.", rating: 4.7, trades: 89,  price: 0.105, amount: "50-2000", currency: "RUB", method: "Тинькофф" },
    { id: 4, user: "Дильноза", rating: 4.8, trades: 67,  price: 0.099, amount: "200-8000", currency: "UZS", method: "Click" },
    { id: 5, user: "Ahmed R.", rating: 4.6, trades: 34,  price: 0.101, amount: "100-3000", currency: "AED", method: "ADCB" },
  ]);
  const [side, setSide] = useState("buy");
  const { setToast } = useStore();

  return (
    <div className="screen">
      <div className="screen-header">
        <h1 className="screen-title">🔄 P2P Exchange</h1>
        <p className="screen-subtitle">TGC ↔ фиат · эскроу защита</p>
      </div>
      <div style={{ padding: 16 }}>
        <div className="grid-2 mb-md">
          <button className={`btn ${side === "buy" ? "btn-teal" : "btn-outline"}`} onClick={() => setSide("buy")}>
            Купить TGC
          </button>
          <button className={`btn ${side === "sell" ? "btn-teal" : "btn-outline"}`} onClick={() => setSide("sell")}>
            Продать TGC
          </button>
        </div>
        {offers.map((o) => (
          <div key={o.id} className="card mb-sm">
            <div className="row between mb-sm">
              <div>
                <div className="text-bold">{o.user}</div>
                <div className="text-sm text-muted">
                  <Star size={11} style={{ verticalAlign: "middle" }} /> {o.rating} · {o.trades} сделок
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="text-bold text-navy">${o.price}</div>
                <div className="text-sm text-hint">за 1 TGC</div>
              </div>
            </div>
            <div className="row between text-sm">
              <span className="text-muted">{o.amount} {o.currency}</span>
              <span className="chip">{o.method}</span>
            </div>
            <button className="btn btn-teal btn-full mt-sm" style={{ padding: 8 }}
              onClick={() => setToast("info", `Сделка создана (DEMO). Эскроу удерживает TGC.`)}>
              {side === "buy" ? "Купить у " + o.user : "Продать " + o.user}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   AI AGENT CHAT
═════════════════════════════════════════════════════════════════ */
export function AIAgentScreen() {
  const { aiChat } = useStore();
  const [messages, setMessages] = useState([
    { role: "ai", text: "Привет! Я Turbo AI 🤖 — твой помощник в экосистеме TGC. Задай любой вопрос: про кошельки, стейкинг, рефералов, Visa-карты, биржу, Marketplace..." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => "tgc_ai_" + Date.now());
  const scrollRef = useRef(null);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input;
    setMessages((m) => [...m, { role: "user", text: userMsg }]);
    setInput("");
    setLoading(true);
    try {
      const r = await aiChat(userMsg, sessionId);
      setMessages((m) => [...m, { role: "ai", text: r.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "⚠️ Извини, я временно недоступен." }]);
    } finally {
      setLoading(false);
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  const suggestions = [
    "Как работает стейкинг?",
    "Что такое Energy Battery?",
    "Как устроена реферальная сеть?",
    "Как получить Visa карту?",
  ];

  return (
    <div className="screen" style={{ display: "flex", flexDirection: "column" }}>
      <div className="screen-header">
        <div className="row gap-sm">
          <div style={{ width: 36, height: 36, borderRadius: 18, background: "linear-gradient(135deg,#0E6B5E,#E8A020)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Bot size={20} color="white" />
          </div>
          <div>
            <div className="text-bold">Turbo AI</div>
            <div className="text-sm" style={{ opacity: 0.7 }}>
              <span style={{ display: "inline-block", width: 6, height: 6, background: "#10B981", borderRadius: 3, marginRight: 4 }} />
              Online · Powered by Claude
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {messages.map((m, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            marginBottom: 12
          }}>
            <div style={{
              maxWidth: "85%",
              background: m.role === "user" ? "var(--navy)" : "white",
              color: m.role === "user" ? "white" : "var(--text)",
              padding: "10px 14px", borderRadius: 16,
              fontSize: 14, lineHeight: 1.5,
              boxShadow: m.role === "ai" ? "0 2px 8px rgba(13,27,75,0.05)" : "none",
              whiteSpace: "pre-wrap",
            }}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && <div style={{ padding: 14, color: "var(--text-hint)", fontSize: 13 }}>Turbo AI печатает...</div>}
        <div ref={scrollRef} />

        {messages.length <= 1 && (
          <div className="mt-md">
            <div className="text-hint text-sm mb-sm">Попробуй спросить:</div>
            {suggestions.map((s, i) => (
              <button key={i} className="btn btn-outline" style={{ width: "100%", marginBottom: 6, fontSize: 13, padding: 10 }}
                onClick={() => setInput(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{
        flexShrink: 0,
        padding: "10px 12px",
        background: "white",
        borderTop: "1px solid var(--border)",
      }}>
        <div className="row gap-sm">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Спроси что-нибудь..."
            className="input"
            style={{ flex: 1, padding: 10 }}
            data-testid="ai-input"
          />
          <button className="btn btn-primary" style={{ padding: 10 }} onClick={send} disabled={loading} data-testid="ai-send">
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Plus(props) { return <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={props.size||16} height={props.size||16}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
